import { Box, Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Form, Formik, useFormikContext } from 'formik';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import { addToast } from '@sophocles/features/toast';
import { applyApiErrorToFormik } from '@sophocles/utils/apiErrors';

// Compute a section-level diff between Formik's current values and
// the initialValues snapshot.  Output contains only sections whose
// contents differ — the consolidated PATCH endpoint accepts every
// section as optional, so unchanged sections are simply omitted.
const computeSectionDiff = (current, initial) => {
  const diff = {};
  for (const key of Object.keys(current)) {
    if (!_.isEqual(current[key], initial[key])) {
      diff[key] = current[key];
    }
  }
  return diff;
};

// Mirror AppNav's leftMargin computation so the fixed SaveBar lines
// up with the content area regardless of LeftNav state.  AppNav
// stores the collapsed flag in localStorage but doesn't dispatch a
// custom event on toggle, so we poll on a short interval — cheap,
// only runs while the page is mounted, and the ~100ms latency is
// invisible against the ~450ms LeftNav transition.
const useLeftNavWidth = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [collapsed, setCollapsed] = React.useState(
    () => typeof window !== 'undefined' && localStorage.getItem('sidebarCollapsed') === 'true',
  );
  React.useEffect(() => {
    const tick = () => {
      const next = localStorage.getItem('sidebarCollapsed') === 'true';
      setCollapsed(prev => (prev === next ? prev : next));
    };
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, []);
  if (isMobile) return 0;
  return collapsed ? 64 : 256;
};

// Save Changes button rendered inside the Formik provider so it can
// read dirty/submitting state.  Disabled when nothing has changed
// or while a save is in flight.
//
// Uses `position: fixed` (rather than sticky) for two reasons:
//   1. Avoids Chrome's flaky sticky-inside-flex behavior, which had
//      the bar mid-scrolling instead of pinning to viewport bottom.
//   2. Lets us pin to the actual viewport edge instead of the
//      AppNav scroll container's content edge — AppNav has a 16px
//      bottom padding that left a visible gap with sticky.
//
// The form gets a corresponding bottom padding (rendered as the
// SaveBarSpacer) so the last bit of content can scroll past the bar.
function SaveBar({ saveLabel, savingLabel, isSaveDisabled }) {
  const { t } = useTranslation();
  const { dirty, initialValues, isSubmitting, isValid, resetForm, values } = useFormikContext();
  // `isValid` is true when the validationSchema currently passes —
  // disable Save while the form is invalid so the user can't fire a
  // PATCH that the backend will just reject with the same message.
  // Formik still flags every offending field with inline errors via
  // SettingsTextField's helperText.
  const disabled = !dirty || isSubmitting || !isValid || Boolean(isSaveDisabled?.(values, initialValues));
  const leftNavWidth = useLeftNavWidth();
  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: `${leftNavWidth}px`,
        right: 0,
        // Modest z so the bar occludes scrolling content but doesn't
        // sit on top of MUI dropdowns/dialogs (which use higher z).
        zIndex: 2,
        // Solid paper background + top border + lift shadow so the
        // bar reads as a distinct surface and clearly separates from
        // content scrolling behind.
        backgroundColor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        boxShadow: 'var(--shadow-raised-top)',
        px: { xs: 2, md: 3 },
        py: 2,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="flex-end"
      >
        {/*
          One validity covers the whole page while a reader is looking at one
          section, so an offending field can sit off screen and the button
          just refuses. Say which way to look rather than leaving the reader
          to guess whether the page is broken.
        */}
        {dirty && !isValid ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1, textAlign: { sm: 'right' } }}>
            {t('settings.fixFieldsToSave', 'Something on this page needs fixing before it can be saved.')}
          </Typography>
        ) : null}
        <Button variant="text" onClick={() => resetForm()} disabled={disabled}>
          {t('actions.cancel', 'Cancel')}
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={disabled}>
          {isSubmitting ? savingLabel : saveLabel}
        </Button>
      </Stack>
    </Box>
  );
}

// Top-level form wrapper for a settings page.
//
// `initialValues` is the sectioned object loaded from the server.
// `onSave(diff)` receives only the changed sections and returns a
// Promise that resolves when the save round-trip completes. Returning
// false means a command lease rejected the write, so the form stays dirty.
// Save outcomes (success and error) surface as snackbars; the SaveBar itself
// stays neutral.
export default function SettingsForm({
  initialValues,
  validationSchema,
  onSave,
  saveLabel,
  savingLabel,
  readOnly = false,
  isSaveDisabled,
  children,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [nonFieldMessages, setNonFieldMessages] = React.useState([]);

  const handleSubmit = async (values, helpers) => {
    if (readOnly) {
      helpers.setSubmitting(false);
      return;
    }
    const diff = computeSectionDiff(values, initialValues);
    if (Object.keys(diff).length === 0) {
      helpers.setSubmitting(false);
      return;
    }
    setNonFieldMessages([]);
    try {
      const saved = await onSave(diff);
      if (saved === false) {
        helpers.setSubmitting(false);
        return;
      }
      // Reset the post-save baseline to the just-saved values, restoring
      // every synthetic `__pending_*` field to its declared initial shape.
      // Synthetic fields include booleans, nulls, and structured change sets;
      // retaining their submitted values would leave the Save bar dirty.
      const clearedValues = { ...values };
      for (const key of Object.keys(clearedValues)) {
        if (key.startsWith('__pending_')) clearedValues[key] = initialValues[key];
      }
      helpers.resetForm({ values: clearedValues });
      dispatch(addToast({ message: t('settings.saved', 'Settings saved.'), level: 'success' }));
    } catch (err) {
      const messages = applyApiErrorToFormik(err, {
        setErrors: helpers.setErrors,
        // Section fields are Formik-nested (e.g. "general.name"); setErrors
        // stores a flat literal key that Formik's getIn can't resolve through
        // a dotted path, so field-prefixed details downgrade to the banner.
        knownFields: [],
        genericMessage: t('errors.unexpected', 'Something went wrong. Please try again.'),
      });
      setNonFieldMessages(messages);
      helpers.setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <Form>
        <Stack spacing={3}>{children}</Stack>
        <FormErrorAlert messages={nonFieldMessages} sx={{ mt: 2 }} />
        {/*
          Spacer at the end of the form so the last section's content
          can scroll all the way past the fixed SaveBar — without
          this, the bar permanently covers the bottom ~88px of the
          last section.  Approximate bar height: py:2 (32px) +
          button height (~36px) + borderTop (1px) ≈ 88px on desktop;
          a bit more on mobile when the bar stacks vertically.
        */}
        {readOnly ? null : <Box sx={{ height: { xs: 140, sm: 100 } }} aria-hidden />}
        {readOnly ? null : (
          <SaveBar
            saveLabel={saveLabel || t('userSettings.saveChanges', 'Save Changes')}
            savingLabel={savingLabel || t('userSettings.saving', 'Saving...')}
            isSaveDisabled={isSaveDisabled}
          />
        )}
      </Form>
    </Formik>
  );
}
